import { LiveChatAppSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { useUpdateLiveChatAppSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateLiveChatAppSettings";
import { transformLiveChatAppSettingsToFormValues } from "@jugl-web/domain-resources/chat-bot/utils/transformLiveChatAppSettingsToFormValues";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { LoadingSpinner, MultiSectionLayout } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { LiveChatAppPreview } from "../../../../components/LiveChatAppPreview";
import { ChatbotPreviewBackground } from "../../../WebChatBotAccountScreen/components/ChatbotPreviewBackground";
import { INITIAL_FORM_VALUES } from "../../consts";

interface AppearanceSettingsTabShellProps {
  tabId: string;
  children: ReactNode;
}

export const AppearanceSettingsTabShell: FC<
  AppearanceSettingsTabShellProps
> = ({ tabId, children }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();

  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();

  const { liveChatAppAccount, updateLiveChatAppSettings } =
    useUpdateLiveChatAppSettings({
      entityId,
    });

  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();

  const form = useForm<LiveChatAppSettingsFormValues>({
    defaultValues: liveChatAppAccount
      ? transformLiveChatAppSettingsToFormValues(liveChatAppAccount.settings)
      : INITIAL_FORM_VALUES,
  });

  const { handleSubmit, reset, control, formState } = form;

  const hasInitializedFormRef = useRef(false);

  const watchedValues = useWatch({ control });

  const previewSuggestions = useMemo(() => {
    const sourceSuggestions =
      (watchedValues.suggestions as { id: string; value: string }[]) ?? [];

    return sourceSuggestions
      .map((suggestion) => suggestion.value.trim())
      .filter((value) => value.length > 0);
  }, [watchedValues.suggestions]);

  const previewCtaTitle = useMemo(() => {
    const fromCtas = watchedValues.ctas?.[0]?.trim() ?? "";
    const fromWelcome = watchedValues.welcomeMessage?.trim() ?? "";

    return (
      fromCtas ||
      fromWelcome ||
      INITIAL_FORM_VALUES.ctas[0] ||
      INITIAL_FORM_VALUES.welcomeMessage
    );
  }, [watchedValues.ctas, watchedValues.welcomeMessage]);

  const previewCtaSubtitle = useMemo(() => {
    const mainTitle = watchedValues.ctas?.[0]?.trim() ?? "";
    const additionalText = watchedValues.ctas?.[1]?.trim() ?? "";

    if (!mainTitle || !additionalText) {
      return "";
    }

    return additionalText;
  }, [watchedValues.ctas]);

  const previewCustomButton = useMemo(() => {
    const title = watchedValues.customButton?.title?.trim() ?? "";
    const url = watchedValues.customButton?.url?.trim() ?? "";

    if (!title || !url) {
      return null;
    }

    return { title, url };
  }, [watchedValues.customButton]);

  const isCustomButtonOnlyOneFieldFilled = useMemo(() => {
    const title = watchedValues.customButton?.title?.trim() ?? "";
    const url = watchedValues.customButton?.url?.trim() ?? "";

    return Boolean(title) !== Boolean(url);
  }, [watchedValues.customButton]);

  const isChatStarterAdditionalTextWithoutMainTitle = useMemo(() => {
    const mainTitle = watchedValues.ctas?.[0]?.trim() ?? "";
    const additionalText = watchedValues.ctas?.[1]?.trim() ?? "";

    return Boolean(additionalText) && !mainTitle;
  }, [watchedValues.ctas]);

  const resetForm = useCallback(() => {
    if (!liveChatAppAccount) {
      return;
    }

    reset(
      transformLiveChatAppSettingsToFormValues(liveChatAppAccount.settings)
    );
  }, [liveChatAppAccount, reset]);

  const handleSaveChanges = useCallback(
    async (formData: LiveChatAppSettingsFormValues) => {
      const removeEmptyItems = (
        items: { id: string; value: string }[]
      ): string[] =>
        items.map((item) => item.value.trim()).filter((v) => v.length > 0);

      const mainTitle = formData.ctas[0]?.trim() ?? "";
      const ctaTitle = (mainTitle || formData.welcomeMessage).trim();
      const additionalText = formData.ctas[1]?.trim() ?? "";
      const ctaSubtitle = mainTitle && additionalText ? additionalText : "";

      const customButtonTitle = formData.customButton.title.trim();
      const customButtonUrl = formData.customButton.url.trim();

      try {
        setIsSubmitting(true);

        await updateLiveChatAppSettings({
          browserTabTitle: formData.browserTabTitle.trim(),
          faviconUrl: formData.faviconUrl,
          businessName: formData.businessName.trim(),
          businessLogoUrl: formData.businessLogoUrl,
          agentName: formData.agentName.trim(),
          agentImage: formData.agentImage,
          theme: formData.theme,
          senderMessageBackgroundColor: formData.senderMessageBackgroundColor,
          ctaTitle,
          ctaSubtitle,
          ctaVisual: formData.ctaVisual,
          customButton:
            customButtonTitle && customButtonUrl
              ? { title: customButtonTitle, url: customButtonUrl }
              : null,
          suggestions: removeEmptyItems(formData.suggestions),
        });

        reset(formData);

        toast(
          t({
            id: "feedback.live-chat-was-updated",
            defaultMessage: "Live Chat was updated",
          })
        );
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [reset, setIsSubmitting, t, toast, updateLiveChatAppSettings]
  );

  useEffect(() => {
    hasInitializedFormRef.current = false;
  }, [entityId]);

  useEffect(() => {
    if (!liveChatAppAccount || hasInitializedFormRef.current) {
      return;
    }

    resetForm();
    hasInitializedFormRef.current = true;
  }, [liveChatAppAccount, resetForm]);

  useEffect(() => {
    return registerDirtySource(tabId, formState.isDirty);
  }, [formState.isDirty, registerDirtySource, tabId]);

  return (
    <MultiSectionLayout
      className="animate-fade-in"
      leftSide={
        <MultiSectionLayout
          classNames={{
            content:
              "border-grey-300 flex h-full w-[464px] shrink-0 flex-col border-0 border-r border-solid bg-white",
          }}
          footer={
            formState.isDirty && (
              <div className="border-grey-300 flex gap-4 border-b-0 border-l-0 border-r border-t border-solid bg-white px-8 pb-6 pt-4">
                <Button
                  variant="tertiary"
                  size="md"
                  className="flex-1"
                  onClick={() => requestNavigation(resetForm)}
                >
                  {t({
                    id: "common.cancel",
                    defaultMessage: "Cancel",
                  })}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  className="flex-[2_1_0%]"
                  onClick={handleSubmit(handleSaveChanges)}
                  isDisabled={
                    isSubmitting ||
                    isCustomButtonOnlyOneFieldFilled ||
                    isChatStarterAdditionalTextWithoutMainTitle
                  }
                  iconEnd={
                    isSubmitting ? (
                      <LoadingSpinner size="xs" />
                    ) : (
                      <ArrowRightIcon />
                    )
                  }
                >
                  {t({
                    id: "common.save-changes",
                    defaultMessage: "Save changes",
                  })}
                </Button>
              </div>
            )
          }
        >
          <FormProvider {...form}>
            <div className="jugl__custom-scrollbar flex h-full flex-col overflow-y-auto px-6 py-10">
              {children}
            </div>
          </FormProvider>
        </MultiSectionLayout>
      }
    >
      <ChatbotPreviewBackground>
        <div className="h-full w-full p-8">
          <LiveChatAppPreview
            screenMode={
              tabId === "generalInfo" ? "sampleConversation" : "welcome"
            }
            browserTabTitle={
              watchedValues.browserTabTitle ??
              INITIAL_FORM_VALUES.browserTabTitle
            }
            faviconUrl={
              watchedValues.faviconUrl ?? INITIAL_FORM_VALUES.faviconUrl
            }
            businessName={
              watchedValues.businessName ?? INITIAL_FORM_VALUES.businessName
            }
            businessLogoUrl={
              watchedValues.businessLogoUrl ??
              INITIAL_FORM_VALUES.businessLogoUrl
            }
            agentName={watchedValues.agentName ?? INITIAL_FORM_VALUES.agentName}
            agentImage={
              watchedValues.agentImage ?? INITIAL_FORM_VALUES.agentImage
            }
            senderMessageBackgroundColor={
              watchedValues.senderMessageBackgroundColor ??
              INITIAL_FORM_VALUES.senderMessageBackgroundColor
            }
            ctaTitle={previewCtaTitle}
            ctaSubtitle={previewCtaSubtitle}
            ctaVisual={watchedValues.ctaVisual ?? INITIAL_FORM_VALUES.ctaVisual}
            suggestions={previewSuggestions}
            customButton={previewCustomButton}
          />
        </div>
      </ChatbotPreviewBackground>
    </MultiSectionLayout>
  );
};
